import { dateParts, toDateFromParts } from "common/utils/dates";
import { ClientUser } from "common/types";
import { useClinicGroupCountry } from "common/contexts/UIContext";
import { CountryCode } from "common/constants/address";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import PrivacyDisclaimerTooltip from "client/common/components/content/PrivacyDisclaimerTooltip";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { useControlledSubstancesInfoDrawer } from "client/accounts/stackables";
import TextButton from "client/common/components/buttons/TextButton";

import { useClientUserQuery, useUpdateProfileMutation } from "../../hooks";
import ControlledSubstancesForm from "../forms/ControlledSubstancesForm";

const getInitialValues = (clientUser: ClientUser) => {
  const { day, month, year } = clientUser.date_of_birth
    ? dateParts(clientUser.date_of_birth)
    : { day: "", month: "", year: "" };
  return {
    drivers_license_number: clientUser.drivers_license_number ?? "",
    drivers_license_state: clientUser.drivers_license_state ?? "",
    birth_day: `${day}`,
    birth_month: `${month}`,
    birth_year: `${year}`,
  };
};

const AccountControlledSubstancesFieldsUpdateDrawer = () => {
  const { data: clientUser, isLoading, error } = useClientUserQuery();
  const { close } = useCurrentDrawer();
  const { open: openControlledSubstancesInfoDrawer } = useControlledSubstancesInfoDrawer();
  const clinicGroupCountry = useClinicGroupCountry();

  const {
    mutate: updateProfile,
    isLoading: isUpdating,
    isSuccess: isUpdateSuccess,
    error: updateError,
  } = useUpdateProfileMutation({
    onSuccess: close,
  });

  if (isLoading) {
    return <DrawerLayout.Loading />;
  }

  if (!clientUser || error) {
    return <DrawerLayout.FetchError error={error} entityName="account" />;
  }

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title={
          <PrivacyDisclaimerTooltip>
            Update information required for controlled substances
          </PrivacyDisclaimerTooltip>
        }
        description={
          <>
            The following fields may be required to purchase controlled substances. Applicable drugs
            and requirements may differ based on{" "}
            {clinicGroupCountry === CountryCode.CA ? "province" : "state"}.{" "}
            <TextButton
              hasUnderline={true}
              icon="right"
              onClick={openControlledSubstancesInfoDrawer}
            >
              Learn more
            </TextButton>
          </>
        }
        onClose={close}
      />
      <ControlledSubstancesForm
        isLoading={isUpdating || isUpdateSuccess}
        initialValues={getInitialValues(clientUser)}
        error={updateError}
        fieldErrors={{
          drivers_license_number: updateError?.errors.drivers_license_number,
          drivers_license_state: updateError?.errors.drivers_license_state,
          birth_month: updateError?.errors.date_of_birth,
          birth_day: updateError?.errors.date_of_birth,
          birth_year: updateError?.errors.date_of_birth,
        }}
        onSubmit={(values) =>
          updateProfile({
            ...values,
            date_of_birth: toDateFromParts({
              year: values.birth_year,
              month: values.birth_month,
              day: values.birth_day,
            }),
          })
        }
      />
    </DrawerLayout>
  );
};

export default AccountControlledSubstancesFieldsUpdateDrawer;
